import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { apiService } from "../services/api";
import toast from "react-hot-toast";
import {
  DocumentArrowDownIcon,
  UsersIcon,
  EyeIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";

const EvaluationResults = () => {
  const { evalId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [results, setResults] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);

  const loadResults = useCallback(async () => {
    try {
      const [evalData, resultsData] = await Promise.all([
        apiService.getEvaluation(evalId),
        apiService.getEvaluationResults(evalId),
      ]);

      setEvaluation(evalData);
      setResults(resultsData);

      // Process results into grouped format
      const grouped = processResults(resultsData);
      setGroupedData(grouped);
    } catch (error) {
      console.error("Error loading results:", error);
      toast.error("Failed to load evaluation results");
    }
    setLoading(false);
  }, [evalId]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const processResults = (resultsData) => {
    const grouped = {};

    resultsData.forEach((result) => {
      if (result.groups_data) {
        result.groups_data.forEach((group) => {
          if (!grouped[group.id]) {
            grouped[group.id] = {
              name: group.name,
              players: [],
            };
          }

          group.players.forEach((player) => {
            const existingPlayer = grouped[group.id].players.find(
              (p) => p.id === player.id
            );
            if (existingPlayer) {
              existingPlayer.evaluatorCount =
                (existingPlayer.evaluatorCount || 1) + 1;
            } else {
              grouped[group.id].players.push({
                ...player,
                evaluatorCount: 1,
                notes: result.player_notes?.[player.id] || "",
              });
            }
          });
        });
      }
    });

    return grouped;
  };

  const exportResults = () => {
    const csvData: string[][] = [];
    csvData.push([
      "Group",
      "Player Name",
      "Jersey Number",
      "Position",
      "Times Placed",
      "Notes",
    ]);

    Object.entries(groupedData).forEach(([groupId, group]) => {
      group.players.forEach((player) => {
        csvData.push([
          group.name,
          player.name,
          player.number,
          player.position || "",
          player.evaluatorCount || 1,
          player.notes || "",
        ]);
      });
    });

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `evaluation-results-${evaluation?.name || "results"}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Results exported successfully!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {evaluation?.name} - Results
          </h1>
          <div className="flex items-center space-x-4 mt-2">
            <Badge variant="blue">
              {results.length} Evaluator{results.length !== 1 ? "s" : ""}
            </Badge>
            <span className="text-gray-600">
              Completed: {new Date(evaluation?.updated_at).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => navigate(`/portal/evaluation/${evalId}/skill-results`)}>
            <AcademicCapIcon className="w-4 h-4 mr-2" />
            Skill Results
          </Button>
          <Button onClick={exportResults}>
            <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
            Export Results
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <UsersIcon className="h-8 w-8 text-slate-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Evaluators
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {results.length}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Players Evaluated
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.values(groupedData).reduce(
                    (total, group) => total + group.players.length,
                    0
                  )}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-semibold text-sm">G</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Groups Created
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.keys(groupedData).length}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Results by Group */}
      <div className="space-y-6">
        {Object.entries(groupedData).map(([groupId, group]) => (
          <Card key={groupId}>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {group.name} ({group.players.length} players)
              </h2>

              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Player
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Consensus
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {group.players.map((player) => (
                      <tr key={`${groupId}-${player.id}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {player.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          #{player.number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {player.position || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm text-gray-900">
                              {player.evaluatorCount}/{results.length}
                            </div>
                            <div className="ml-2">
                              {player.evaluatorCount === results.length ? (
                                <Badge variant="green">Unanimous</Badge>
                              ) : player.evaluatorCount > results.length / 2 ? (
                                <Badge variant="yellow">Majority</Badge>
                              ) : (
                                <Badge variant="gray">Split</Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {player.notes || "-"}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {Object.keys(groupedData).length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-gray-400 text-2xl">📊</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No results yet
          </h3>
          <p className="text-gray-600">
            Results will appear here once evaluators complete their assessments.
          </p>
        </div>
      )}
    </div>
  );
};

export default EvaluationResults;
